import { z } from 'zod';
import { Room } from './house.js';
import { GlobalPrivilege, InviteStatus, ScheduleScope } from './invite.js';
/** A trimmed HA state object. Attributes are passed through as-is. */
export const EntityState = z.object({
    entityId: z.string(),
    state: z.string(),
    attributes: z.record(z.string(), z.unknown()),
    lastChanged: z.string(),
});
/** What a redeemed guest session is allowed to see. Sent once on connect. */
export const GuestContext = z.object({
    guestName: z.string(),
    guestRoomId: z.string().nullable(),
    endsAt: z.iso.datetime(),
    startsAt: z.iso.datetime(),
    status: InviteStatus,
    globals: z.array(GlobalPrivilege),
    /** Rooms already filtered down to the controls this guest may use. */
    rooms: z.array(Room),
    alarmEntityId: z.string().nullable(),
    frontDoorLockEntityId: z.string().nullable(),
    frontDoorContactEntityId: z.string().nullable(),
    houseName: z.string(),
    /** Whether the House-sitting notes tab should appear at all. */
    hasHouseInfo: z.boolean(),
    /** How much of the lighting schedule this guest may set. */
    scheduleScope: ScheduleScope,
    /** How many timing settings they have, so Home can size its card honestly. */
    scheduleCount: z.number().int().nonnegative(),
    /** Null when no Wi-Fi is configured, so the UI simply omits the card. */
    wifi: z
        .object({
        ssid: z.string(),
        password: z.string(),
        security: z.enum(['WPA', 'WEP', 'nopass']),
    })
        .nullable(),
});
// ---------------------------------------------------------------- WebSocket
export const ServerMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('hello'), context: GuestContext }),
    z.object({ type: z.literal('snapshot'), states: z.array(EntityState) }),
    z.object({ type: z.literal('state'), state: EntityState }),
    z.object({ type: z.literal('error'), message: z.string() }),
    z.object({ type: z.literal('expired') }),
    z.object({ type: z.literal('pong') }),
]);
export const ClientMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ping') }),
    z.object({ type: z.literal('resync') }),
]);
// ------------------------------------------------------------------ Actions
/**
 * Guest-initiated actions. Deliberately a closed set rather than a raw HA
 * service-call passthrough — the server maps each to a service and re-checks
 * the entity against the invite before calling.
 */
export const GuestAction = z.discriminatedUnion('action', [
    z.object({ action: z.literal('toggle'), entityId: z.string() }),
    z.object({ action: z.literal('turn_on'), entityId: z.string() }),
    z.object({ action: z.literal('turn_off'), entityId: z.string() }),
    z.object({ action: z.literal('set_brightness'), entityId: z.string(), brightness: z.number().int().min(0).max(255) }),
    z.object({ action: z.literal('set_color_temp'), entityId: z.string(), kelvin: z.number().int().min(1500).max(8000) }),
    z.object({ action: z.literal('set_hvac_mode'), entityId: z.string(), mode: z.string() }),
    z.object({ action: z.literal('set_temperature'), entityId: z.string(), temperature: z.number().min(5).max(35) }),
    z.object({ action: z.literal('set_fan_mode'), entityId: z.string(), mode: z.string() }),
    z.object({ action: z.literal('set_preset'), entityId: z.string(), preset: z.string() }),
    z.object({ action: z.literal('cover_open'), entityId: z.string() }),
    z.object({ action: z.literal('cover_close'), entityId: z.string() }),
    z.object({ action: z.literal('cover_stop'), entityId: z.string() }),
    z.object({ action: z.literal('cover_position'), entityId: z.string(), position: z.number().int().min(0).max(100) }),
    z.object({ action: z.literal('media_play_pause'), entityId: z.string() }),
    z.object({ action: z.literal('media_next'), entityId: z.string() }),
    z.object({ action: z.literal('media_previous'), entityId: z.string() }),
    z.object({ action: z.literal('media_volume'), entityId: z.string(), level: z.number().min(0).max(1) }),
    // Sonos grouping. `members` are the speakers to pull into this one's group;
    // every one of them is authorised separately, server-side.
    z.object({ action: z.literal('media_join'), entityId: z.string(), members: z.array(z.string()).min(1).max(16) }),
    z.object({ action: z.literal('media_unjoin'), entityId: z.string() }),
    z.object({ action: z.literal('select_option'), entityId: z.string(), option: z.string() }),
    z.object({ action: z.literal('set_number'), entityId: z.string(), value: z.number() }),
    z.object({ action: z.literal('press'), entityId: z.string() }),
    z.object({ action: z.literal('fan_speed'), entityId: z.string(), percentage: z.number().int().min(0).max(100) }),
    z.object({ action: z.literal('unlock'), entityId: z.string() }),
    z.object({ action: z.literal('lock'), entityId: z.string() }),
    z.object({ action: z.literal('alarm_disarm'), entityId: z.string(), code: z.string().optional() }),
    // Away is the only arm mode the house actually distinguishes, so it is the
    // only one exposed — home and night were meaningless on this panel.
    z.object({ action: z.literal('alarm_arm_away'), entityId: z.string(), code: z.string().optional() }),
]);
export const RedeemInput = z.object({ code: z.string().min(4).max(32) });
export const AdminLoginInput = z.object({ password: z.string().min(1) });
export const AuditEntry = z.object({
    id: z.number().int(),
    at: z.iso.datetime(),
    inviteId: z.string().nullable(),
    guestName: z.string().nullable(),
    kind: z.string(),
    entityId: z.string().nullable(),
    detail: z.string(),
    allowed: z.boolean(),
});
//# sourceMappingURL=protocol.js.map